/**
 * Demo data module — provides fake contractor results and ZIP codes
 * for the preview/demo splash experience.
 *
 * All data is attributed to "DEMO Sealcoat & Paving Services XYZ CO."
 * to make it obvious this isn't real directory content.
 */

export interface DemoContractor {
  id: string
  name: string
  city: string
  state: string
  zip: string
  rating: number
  reviews: number
  phone: string
  services: string[]
  tier: 'Diamond' | 'Gold' | 'Silver'
}

export interface DemoZip {
  zip: string
  city: string
  state: string
  contractors: DemoContractor[]
}

const DEMO_COMPANY = 'DEMO Sealcoat & Paving Services XYZ CO.'

function demoContractor(
  id: string,
  suffix: string,
  city: string,
  state: string,
  zip: string,
  rating: number,
  reviews: number,
  tier: 'Diamond' | 'Gold' | 'Silver',
  services: string[]
): DemoContractor {
  return {
    id: `demo-${id}`,
    name: `${DEMO_COMPANY} — ${suffix}`,
    city,
    state,
    zip,
    rating,
    reviews,
    phone: '(555) 000-0000',
    services,
    tier,
  }
}

export const DEMO_SERVICE_TYPES = [
  { key: 'sealcoating', label: 'Sealcoating', description: 'Connect with trusted sealcoating professionals in your area' },
  { key: 'crack-filling', label: 'Crack Filling', description: 'Find experts for professional crack sealing and repair' },
  { key: 'striping', label: 'Striping & Line Marking', description: 'Connect with professional striping and marking specialists' },
  { key: 'asphalt-repair', label: 'Asphalt Repair', description: 'Find skilled professionals for asphalt repair and restoration' },
  { key: 'resurfacing', label: 'Resurfacing', description: 'Connect with resurfacing and overlay specialists' },
  { key: 'cost-estimate', label: 'Cost Estimator', description: 'Get general cost estimates to help plan your project budget' },
] as const

export type DemoServiceKey = typeof DEMO_SERVICE_TYPES[number]['key']

export const DEMO_ZIPS: DemoZip[] = [
  {
    zip: '10001', city: 'New York', state: 'NY',
    contractors: [
      demoContractor('ny1', 'Manhattan Division', 'New York', 'NY', '10001', 4.9, 142, 'Diamond', ['Sealcoating', 'Crack Filling', 'Striping']),
      demoContractor('ny2', 'Borough Pro Team', 'New York', 'NY', '10001', 4.7, 89, 'Gold', ['Asphalt Repair', 'Resurfacing']),
      demoContractor('ny3', 'Metro Paving Unit', 'New York', 'NY', '10001', 4.6, 67, 'Silver', ['Sealcoating', 'Asphalt Repair']),
    ],
  },
  {
    zip: '90210', city: 'Beverly Hills', state: 'CA',
    contractors: [
      demoContractor('ca1', 'SoCal Premium', 'Beverly Hills', 'CA', '90210', 4.9, 203, 'Diamond', ['Sealcoating', 'Resurfacing', 'Striping']),
      demoContractor('ca2', 'Pacific Coast Crew', 'Beverly Hills', 'CA', '90210', 4.8, 156, 'Gold', ['Crack Filling', 'Asphalt Repair']),
      demoContractor('ca3', 'Westside Paving', 'Beverly Hills', 'CA', '90210', 4.5, 72, 'Silver', ['Sealcoating', 'Crack Filling']),
    ],
  },
  {
    zip: '60601', city: 'Chicago', state: 'IL',
    contractors: [
      demoContractor('il1', 'Windy City Division', 'Chicago', 'IL', '60601', 4.8, 178, 'Diamond', ['Sealcoating', 'Striping', 'Resurfacing']),
      demoContractor('il2', 'Lakefront Paving', 'Chicago', 'IL', '60601', 4.7, 112, 'Gold', ['Asphalt Repair', 'Crack Filling']),
      demoContractor('il3', 'Loop Surface Pros', 'Chicago', 'IL', '60601', 4.5, 54, 'Silver', ['Sealcoating']),
    ],
  },
  {
    zip: '77001', city: 'Houston', state: 'TX',
    contractors: [
      demoContractor('tx1', 'Texas Flagship', 'Houston', 'TX', '77001', 4.9, 198, 'Diamond', ['Sealcoating', 'Resurfacing', 'Asphalt Repair']),
      demoContractor('tx2', 'Gulf Coast Team', 'Houston', 'TX', '77001', 4.6, 91, 'Gold', ['Crack Filling', 'Striping']),
      demoContractor('tx3', 'Bayou Paving Co', 'Houston', 'TX', '77001', 4.4, 43, 'Silver', ['Sealcoating']),
    ],
  },
  {
    zip: '85001', city: 'Phoenix', state: 'AZ',
    contractors: [
      demoContractor('az1', 'Desert Sun Division', 'Phoenix', 'AZ', '85001', 4.8, 134, 'Diamond', ['Sealcoating', 'Crack Filling']),
      demoContractor('az2', 'Valley Asphalt', 'Phoenix', 'AZ', '85001', 4.7, 88, 'Gold', ['Resurfacing', 'Asphalt Repair']),
      demoContractor('az3', 'Sonoran Paving', 'Phoenix', 'AZ', '85001', 4.5, 51, 'Silver', ['Striping', 'Sealcoating']),
    ],
  },
  {
    zip: '30301', city: 'Atlanta', state: 'GA',
    contractors: [
      demoContractor('ga1', 'Peach State Pros', 'Atlanta', 'GA', '30301', 4.9, 165, 'Diamond', ['Sealcoating', 'Resurfacing']),
      demoContractor('ga2', 'ATL Surface Team', 'Atlanta', 'GA', '30301', 4.6, 77, 'Gold', ['Crack Filling', 'Striping']),
      demoContractor('ga3', 'Southern Paving Co', 'Atlanta', 'GA', '30301', 4.4, 38, 'Silver', ['Asphalt Repair']),
    ],
  },
  {
    zip: '98101', city: 'Seattle', state: 'WA',
    contractors: [
      demoContractor('wa1', 'Pacific NW Division', 'Seattle', 'WA', '98101', 4.8, 143, 'Diamond', ['Sealcoating', 'Crack Filling', 'Resurfacing']),
      demoContractor('wa2', 'Emerald City Paving', 'Seattle', 'WA', '98101', 4.7, 96, 'Gold', ['Striping', 'Asphalt Repair']),
      demoContractor('wa3', 'Puget Sound Asphalt', 'Seattle', 'WA', '98101', 4.5, 59, 'Silver', ['Sealcoating']),
    ],
  },
  {
    zip: '33101', city: 'Miami', state: 'FL',
    contractors: [
      demoContractor('fl1', 'Sunshine State Team', 'Miami', 'FL', '33101', 4.9, 187, 'Diamond', ['Sealcoating', 'Resurfacing', 'Striping']),
      demoContractor('fl2', 'Ocean Drive Paving', 'Miami', 'FL', '33101', 4.7, 104, 'Gold', ['Crack Filling', 'Asphalt Repair']),
      demoContractor('fl3', 'Coastal Surface Pros', 'Miami', 'FL', '33101', 4.5, 48, 'Silver', ['Sealcoating']),
    ],
  },
  {
    zip: '80201', city: 'Denver', state: 'CO',
    contractors: [
      demoContractor('co1', 'Mile High Division', 'Denver', 'CO', '80201', 4.8, 155, 'Diamond', ['Sealcoating', 'Resurfacing']),
      demoContractor('co2', 'Rocky Mountain Paving', 'Denver', 'CO', '80201', 4.6, 82, 'Gold', ['Crack Filling', 'Striping']),
      demoContractor('co3', 'Front Range Asphalt', 'Denver', 'CO', '80201', 4.4, 41, 'Silver', ['Asphalt Repair']),
    ],
  },
  {
    zip: '37201', city: 'Nashville', state: 'TN',
    contractors: [
      demoContractor('tn1', 'Music City Pros', 'Nashville', 'TN', '37201', 4.9, 132, 'Diamond', ['Sealcoating', 'Crack Filling', 'Resurfacing']),
      demoContractor('tn2', 'Volunteer Paving', 'Nashville', 'TN', '37201', 4.7, 74, 'Gold', ['Striping', 'Asphalt Repair']),
      demoContractor('tn3', 'Cumberland Surface Co', 'Nashville', 'TN', '37201', 4.5, 36, 'Silver', ['Sealcoating']),
    ],
  },
]

/** Look up demo contractors for a given ZIP. Returns null if ZIP isn't in demo set. */
export function getDemoResultsForZip(zip: string): DemoZip | null {
  return DEMO_ZIPS.find(d => d.zip === zip) ?? null
}
